import { useEffect, useMemo, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useAuthStore } from "./stores/auth.store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Role } from "./types/auth.type";
import { getHomeRoute } from "./utils/roles";
import Login from "./pages/Login";
import Users from "./pages/Users";
import Units from "./pages/Units";
import Schedule from "./pages/Schedule";
import Workouts from "./pages/Workouts";
import WorkoutCatalog from "./pages/WorkoutCatalog";
import ProtectedRoute from "./components/ProtectedRoute";

const queryClient = new QueryClient();

const baseTheme = {
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 16px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: "1px solid",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid",
        },
      },
    },
  },
};

function AppRoutes({ onThemeToggle }: { onThemeToggle: () => void }) {
  const { isAuthenticated, isAdmin, isManager, isCoach } = useAuthStore();
  const homeRoute = getHomeRoute(isAdmin, isManager, isCoach);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to={homeRoute} replace /> : <Login />
        }
      />
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to={homeRoute} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute
            allowedRoles={[Role.ADMIN]}
            redirectTo="/workouts"
            onThemeToggle={onThemeToggle}
          >
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/units"
        element={
          <ProtectedRoute
            allowedRoles={[Role.ADMIN]}
            redirectTo="/workouts"
            onThemeToggle={onThemeToggle}
          >
            <Units />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute
            allowedRoles={[Role.ADMIN, Role.MANAGER]}
            redirectTo="/schedule"
            onThemeToggle={onThemeToggle}
          >
            <Schedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workouts"
        element={
          <ProtectedRoute
            allowedRoles={[Role.ADMIN, Role.COACH]}
            onThemeToggle={onThemeToggle}
          >
            <Workouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workout-catalog"
        element={
          <ProtectedRoute
            allowedRoles={[Role.ADMIN]}
            redirectTo="/schedule"
            onThemeToggle={onThemeToggle}
          >
            <WorkoutCatalog />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const { initAuth } = useAuthStore();
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem("themeMode");
    return (
      (savedMode as "light" | "dark") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    );
  });

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        palette: {
          mode,
          primary: {
            main: "#2563EB",
            light: "#3B82F6",
            dark: "#1D4ED8",
            contrastText: "#FFFFFF",
          },
          secondary: {
            main: "#06B6D4",
            light: "#22D3EE",
            dark: "#0891B2",
            contrastText: "#FFFFFF",
          },
          background: {
            default: mode === "dark" ? "#0F172A" : "#F8FAFC",
            paper: mode === "dark" ? "#1E293B" : "#FFFFFF",
          },
          text: {
            primary: mode === "dark" ? "#F8FAFC" : "#1E293B",
            secondary: mode === "dark" ? "#94A3B8" : "#64748B",
          },
          divider:
            mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.06)",
        },
        components: {
          ...baseTheme.components,
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                color: mode === "dark" ? "#F8FAFC" : "#1E293B",
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                boxShadow:
                  mode === "dark"
                    ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                    : "0 4px 20px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    mode === "dark"
                      ? "0 8px 30px rgba(0, 0, 0, 0.3)"
                      : "0 8px 30px rgba(0, 0, 0, 0.1)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                transition: "all 0.3s ease-in-out",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                padding: "8px 16px",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                },
              },
              contained: {
                background: `linear-gradient(90deg, #2563EB 0%, #06B6D4 100%)`,
                "&:hover": {
                  background: `linear-gradient(90deg, #1D4ED8 0%, #0891B2 100%)`,
                  boxShadow: "0 4px 20px rgba(37, 99, 235, 0.35)",
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    initAuth();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AppRoutes onThemeToggle={toggleTheme} />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
